import { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  ButtonProps,
  Menu,
  MenuItem,
  TooltipProps,
} from "@mui/material";
import ConfirmationModal, {
  ConfirmationModalProps,
} from "./misc/ConfirmationModal";
import CustomTooltip from "./misc/CustomTooltip";
import LoadingIndicator from "./misc/LoadingIndicator";
import SvgIcon, { SvgIconType } from "../helpers/SvgIcon";
import { ActionMenuComponentContext } from "./actionMenu/ActionMenuComponentContext";

interface CustomProps {
  icon?: SvgIconType;
  buttonRef?: (element: HTMLElement | null) => void;
  target?: string;
  download?: string | boolean;
  rel?: string;
  navigate?: string;
  withDefaultAnchor?: boolean;
  tooltipTitle?: string;
  tooltipArrow?: boolean;
  tooltipPosition?: TooltipProps["placement"];
  loading?: boolean;
  loadingSize?: number;
  withConfirmationModal?: {
    modalTitle?: string;
    modalSubtitle?: string;
    modalLeftButtonText?: string;
    modalRightButtonText?: string;
    modalLeftButtonOnClick?: () => void;
    modalRightButtonOnClick?: () => void;
    modalLeftButtonColor?: ConfirmationModalProps["leftButtonColor"];
    modalRightButtonColor?: ConfirmationModalProps["rightButtonColor"];
    modalLeftButtonVariant?: ConfirmationModalProps["leftButtonVariant"];
    modalRightButtonVariant?: ConfirmationModalProps["rightButtonVariant"];
    modalSize?: ConfirmationModalProps["modalSize"];
  };
  withDropdown?: {
    menuItems: {
      value: string | number;
      label: string | number;
      onClick: () => void;
    }[];
    anchorOrigin?: {
      vertical?: "top" | "bottom" | "center";
      horizontal?: "right" | "left" | "center";
    };
  };
}

const CustomButton = (props: ButtonProps & CustomProps) => {
  const {
    icon,
    buttonRef,
    navigate,
    withDefaultAnchor,
    tooltipTitle,
    tooltipArrow = true,
    tooltipPosition = "top-start",
    loading = false,
    loadingSize,
    withConfirmationModal,
    withDropdown,
    children,
    className = "",
    disabled = false,
    onClick,
    href,
    ...buttonProps
  } = props;
  const actionMenuContext = useContext(ActionMenuComponentContext);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isDisabled = disabled || loading;
  const menuOrigin = {
    vertical: withDropdown?.anchorOrigin?.vertical ?? "top",
    horizontal: withDropdown?.anchorOrigin?.horizontal ?? "right",
  } as const;
  const destination = navigate ?? href;
  const linkProps =
    destination && !isDisabled
      ? withDefaultAnchor || !navigate
        ? { href: destination }
        : { component: Link, to: destination }
      : {};

  const handleClick = useCallback<NonNullable<ButtonProps["onClick"]>>(
    (event) => {
      if (isDisabled) {
        event.preventDefault();
        return;
      }
      if (withConfirmationModal) {
        event.preventDefault();
        setShowConfirmationModal(true);
      } else if (withDropdown) {
        event.preventDefault();
        setAnchorEl(actionMenuContext?.getTrigger() ?? event.currentTarget);
      } else {
        onClick?.(event);
      }
    },
    [
      actionMenuContext,
      isDisabled,
      onClick,
      withConfirmationModal,
      withDropdown,
    ],
  );

  useEffect(() => {
    actionMenuContext?.register({ ...props, onClick: handleClick });
  }, [actionMenuContext, props, handleClick]);

  useEffect(() => () => actionMenuContext?.register(null), [actionMenuContext]);

  const trigger = (
    <Button
      {...buttonProps}
      color={
        buttonProps.color ??
        (buttonProps.variant === "action" ? "inherit" : undefined)
      }
      {...linkProps}
      ref={buttonRef}
      disabled={isDisabled}
      onClick={handleClick}
      aria-busy={loading || undefined}
      aria-label={buttonProps["aria-label"] ?? tooltipTitle}
      className={`relative inline-flex items-center justify-center gap-2 ${
        loading
          ? "[&_.MuiButton-startIcon]:invisible [&_.MuiButton-endIcon]:invisible"
          : ""
      } ${className}`}>
      <span
        className={`inline-flex items-center justify-center gap-2 ${
          loading ? "opacity-0" : ""
        }`}>
        {icon && (
          <span
            aria-hidden="true"
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center [&_svg]:!h-5 [&_svg]:!w-5">
            <SvgIcon type={icon} fill="currentColor" />
          </span>
        )}
        {children}
      </span>
      {loading && (
        <span
          className="absolute inset-0 flex items-center justify-center"
          aria-hidden="true">
          <LoadingIndicator size={loadingSize ?? "1.25em"} color="inherit" />
        </span>
      )}
    </Button>
  );

  return (
    <>
      {actionMenuContext ? null : tooltipTitle ? (
        <CustomTooltip
          arrow={tooltipArrow}
          title={showConfirmationModal || anchorEl ? "" : tooltipTitle}
          placement={tooltipPosition}
          PopperProps={{
            modifiers: [{ name: "offset", options: { offset: [0, 8] } }],
          }}>
          <span
            className={
              buttonProps.fullWidth ? "inline-flex w-full" : "inline-flex"
            }>
            {trigger}
          </span>
        </CustomTooltip>
      ) : (
        trigger
      )}
      {withConfirmationModal && (
        <ConfirmationModal
          show={showConfirmationModal}
          handleClose={() => setShowConfirmationModal(false)}
          title={withConfirmationModal.modalTitle}
          subtitle={withConfirmationModal.modalSubtitle}
          leftButtonText={withConfirmationModal.modalLeftButtonText}
          leftButtonOnClick={withConfirmationModal.modalLeftButtonOnClick}
          rightButtonText={withConfirmationModal.modalRightButtonText}
          rightButtonOnClick={withConfirmationModal.modalRightButtonOnClick}
          leftButtonColor={withConfirmationModal.modalLeftButtonColor}
          rightButtonColor={withConfirmationModal.modalRightButtonColor}
          leftButtonVariant={withConfirmationModal.modalLeftButtonVariant}
          rightButtonVariant={withConfirmationModal.modalRightButtonVariant}
          modalSize={withConfirmationModal.modalSize}
        />
      )}
      {withDropdown && (
        <Menu
          anchorEl={anchorEl}
          anchorOrigin={menuOrigin}
          transformOrigin={menuOrigin}
          open={Boolean(anchorEl)}
          TransitionProps={{ onExited: actionMenuContext?.restoreFocus }}
          onClose={() => setAnchorEl(null)}>
          {withDropdown.menuItems.map((menuItem) => (
            <MenuItem
              key={menuItem.value}
              disabled={isDisabled}
              onClick={() => {
                setAnchorEl(null);
                // Let the menu close before opening a dialog from its action.
                setTimeout(menuItem.onClick, 400);
              }}>
              {menuItem.label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
};

export default CustomButton;
